import Typography from '@components/_atoms/Typography';
import { TypoVariant } from '@constants/atoms';
import { CHANNEL_TALK, COMPANY_INTRO, HOME, SELLER, SIGNUP } from '@constants/routes/routes';
import styled from '@emotion/styled';
import { blue, gray, primary, white } from '@styles/Colors';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

type FaqAnswer = {
  readonly imageUrl: string;
  readonly link: string;
  readonly linkText: string;
  readonly content: string[];
  readonly activeContentIndex?: number;
  readonly finishContent: string;
};

const FaqQuestionAnswerContainer = styled.div`
  background: #fafafa;
`;

const FaqQuestionAnswerWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1080px;
  padding: 60px 0;
  gap: 20px;
  margin: 0 auto;
`;

const FaqQuestionCard = styled.div`
  display: flex;
  width: 530px;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  flex-shrink: 0;

  border-radius: 12px;
  background: ${white};
  box-shadow: 0px 2px 4px 0px rgba(128, 128, 128, 0.08);
`;

const FaqQuestionTextListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const FaqQuestionTextWrapper = styled.div<{ active?: boolean }>`
  display: flex;
  padding: 20px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  background-color: ${(props) => (props.active ? primary.blue : white)};
  border-radius: 8px;
  cursor: pointer;

  span {
    font-weight: ${(props) => (props.active ? 700 : 500)};
    color: ${(props) => (props.active ? white : primary.gray)};
  }

  ${(props) =>
    !props.active &&
    `
  &:hover {
    background-color: ${blue.blue3};
    
    span {
      color: ${white};
    }
  }
`}
`;

const FaqAnswerCard = styled.div`
  display: flex;
  width: 530px;
  height: 524px;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  flex-shrink: 0;

  border-radius: 12px;
  background: ${white};
  box-shadow: 0px 2px 4px 0px rgba(128, 128, 128, 0.08);
`;

const FaqAnswerCardLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
  padding-left: 40px;
  padding-right: 60px;
  padding-top: 40px;

  a {
    color: ${blue.blue3};

    font-family: Pretendard;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 30px;
    letter-spacing: -0.4px;
    text-decoration-line: underline;
  }
`;

const FaqAnswerCardContentWrapper = styled.div`
  width: 100%;
  text-align: right;

  display: flex;
  flex-direction: column;
  gap: 20px;

  padding-left: 48px;
  padding-right: 60px;
  padding-bottom: 80px;
`;

const FaqAnswerCardContentTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const FaqAnswerCardContentText = styled.p`
  color: ${primary.gray};

  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 34px;
  letter-spacing: -0.48px;
  white-space: pre-line;
`;

const questionList: string[] = [
  'Q1 기업 전용 아이디가 필요해요',
  'Q2 steelboso 서비스가 궁금해요',
  'Q3 거래 중 발생하는 문서를 받아보고 싶어요',
  'Q4 steelboso와 인터뷰하고 싶어요',
  'Q5 견적을 좀 더 빠르게 받아보고 싶어요',
];

// TODO : 링크 처리
const answerList: FaqAnswer[] = [
  {
    imageUrl: '/assets/images/best_user.png',
    link: SIGNUP,
    linkText: '회원가입 바로가기',
    content: [
      '담당자와의 소통이 필요하기 때문에\n회사에 소속된 직원이 회사 인증을 통해\n',
      '개인 회원의 형태로 가입만 가능',
      '합니다',
    ],
    finishContent: '편리함을 위해 이에 대한 논의를 진행중이예요',
  },
  {
    imageUrl: '/assets/images/best_bag.png',
    link: COMPANY_INTRO,
    linkText: '회사소개 바로가기',
    content: [
      '다양한 데이터를 분석하여 고객님들이\n간편하고 안전하게 스틸 자재를 거래할 수\n있도록 돕는 ',
      '스틸 자재 거래 플랫폼',
      '입니다',
    ],
    finishContent: '온라인 스틸 거래의 모든 과정을 도와드려요',
  },
  {
    imageUrl: '/assets/images/best_pdf_file.png',
    link: SELLER,
    linkText: '프로젝트 관리 바로가기',
    content: ['프로젝트 관리 > 상세 > 견적서\n', '페이지에서 거래문서를 다운받을 수 있어요'],
    activeContentIndex: 0,
    finishContent: '거래문서는 PDF 형식입니다',
  },
  {
    imageUrl: '/assets/images/best_sofa.png',
    link: HOME,
    linkText: '스틸보소에 바로가기',
    content: ['인터뷰는 언제나 열려있습니다'],
    finishContent: '이메일, 채널톡 채팅 등으로 편히 연락주세요\n우리의 이야기를 들려드릴게요',
  },
  {
    imageUrl: '/assets/images/best_rocket.png',
    link: CHANNEL_TALK,
    linkText: '스틸보소 1:1 문의하기',
    content: ['짧은 기간에 빠른 견적이 필요하신 경우에는\n', '스틸보소 담당자에게 연락', '해주세요.'],
    finishContent: '담당자가 배정되면 좀 더 빠르게\n견적을 받아보실 수 있습니다',
  },
];

export default function FaqQuestionAnswer() {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const currentAnswer = answerList[activeIndex];

  return (
    <>
      <FaqQuestionAnswerContainer>
        <FaqQuestionAnswerWrapper>
          <FaqQuestionCard>
            <Typography variant={TypoVariant.SH1}>
              스틸보소에서 가장 많은 질문을 받았던 <br />
              항목을 정리했어요!
            </Typography>
            <FaqQuestionTextListWrapper>
              {questionList.map((item, index) => (
                <FaqQuestionTextWrapper
                  key={index}
                  active={index === activeIndex}
                  onClick={() => setActiveIndex(index)}>
                  <Typography variant={TypoVariant.MH2}>{item}</Typography>
                  <Image src="/assets/images/arrow_white.png" alt="arrow-image" width={20} height={20} />
                </FaqQuestionTextWrapper>
              ))}
            </FaqQuestionTextListWrapper>
          </FaqQuestionCard>

          <FaqAnswerCard>
            <FaqAnswerCardLinkWrapper>
              <Image src={currentAnswer.imageUrl} alt="answer-image" width={150} height={150} />
              <Link href={currentAnswer.link}>{currentAnswer.linkText}</Link>
            </FaqAnswerCardLinkWrapper>
            <FaqAnswerCardContentWrapper>
              <Typography variant={TypoVariant.SH2} color={gray.gray6}>
                스틸보소는
              </Typography>
              <FaqAnswerCardContentTextWrapper>
                <FaqAnswerCardContentText>
                  {currentAnswer.content.map((item, index) => (
                    <>
                      {index === (currentAnswer.activeContentIndex ?? 1) ? (
                        <Typography variant={TypoVariant.SH1} color={primary.blue}>
                          {item}
                        </Typography>
                      ) : (
                        <>{item}</>
                      )}
                    </>
                  ))}
                </FaqAnswerCardContentText>
                <Typography variant={TypoVariant.SH1} color={primary.gray}>
                  {currentAnswer.finishContent}
                </Typography>
              </FaqAnswerCardContentTextWrapper>
            </FaqAnswerCardContentWrapper>
          </FaqAnswerCard>
        </FaqQuestionAnswerWrapper>
      </FaqQuestionAnswerContainer>
    </>
  );
}
